import type { QueryClient } from "@tanstack/react-query";
import type { WorkerCard, WorkerConversationMessage } from "../types";
import { workersKeys } from "./keys";

export interface WorkerMessagesCache {
  messages: WorkerConversationMessage[];
  total: number;
  hasMore: boolean;
}

/** Patch a worker row in the cached list. */
export function patchWorkerInList(
  queryClient: QueryClient,
  worker: WorkerCard,
): void {
  for (const view of ["summary", "full"] as const) {
    queryClient.setQueryData<WorkerCard[]>(workersKeys.list(view), (prev) => {
      if (!prev) return prev;
      const idx = prev.findIndex((w) => w._id === worker._id);
      if (idx < 0) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], ...worker };
      return next;
    });
  }
  queryClient.setQueryData(workersKeys.card(worker._id), worker);
}

/** Remove a worker from list caches. */
export function removeWorkerFromList(
  queryClient: QueryClient,
  workerId: string,
): void {
  for (const view of ["summary", "full"] as const) {
    queryClient.setQueryData<WorkerCard[]>(workersKeys.list(view), (prev) =>
      prev ? prev.filter((w) => w._id !== workerId) : prev,
    );
  }
  queryClient.removeQueries({ queryKey: workersKeys.card(workerId) });
}

type MessageIdFields = {
  _id?: string;
  id?: string;
};

/** API may return ``_id`` (Mongo) or ``id`` (legacy/Pydantic) for the same message. */
type WorkerConversationMessageInput = Omit<
  WorkerConversationMessage,
  "_id"
> & {
  _id?: string;
  id?: string;
};

function messageId(message: MessageIdFields): string {
  return message._id ?? message.id ?? "";
}

function normalizeWorkerMessage(
  message: WorkerConversationMessageInput,
): WorkerConversationMessage {
  const id = messageId(message);
  if (!id) {
    throw new Error("Worker message is missing id");
  }
  const { id: _legacyId, _id: _mongoId, ...rest } = message;
  return { ...rest, _id: id };
}

/** Stable id for API messages that use either ``_id`` or ``id``. */
export function workerMessageStableId(message: MessageIdFields): string {
  return messageId(message);
}

/** Append a message to the cached conversation messages. */
export function appendWorkerMessage(
  queryClient: QueryClient,
  conversationId: string,
  message: WorkerConversationMessageInput,
): void {
  const normalized = normalizeWorkerMessage(message);
  const normalizedId = messageId(normalized);
  queryClient.setQueryData<WorkerMessagesCache>(
    workersKeys.messages(conversationId),
    (prev) => {
      const current: WorkerMessagesCache = prev ?? {
        messages: [],
        total: 0,
        hasMore: false,
      };
      if (
        normalizedId &&
        current.messages.some((m) => messageId(m) === normalizedId)
      ) {
        return current;
      }
      const messages = [...current.messages, normalized];
      return {
        messages,
        total: Math.max(current.total, messages.length),
        hasMore: current.hasMore,
      };
    },
  );
}
